import React, { useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import classNames from 'classnames';
import { gql } from 'apollo-boost';
import { useQuery } from '@apollo/react-hooks';
import { useQueryParams, useTranslation } from 'hooks';

import { useFeatureFlagState } from 'context';
import { toTitleCase } from 'helpers/string';
import { trackingDocumentListProfileFeed } from 'services';

import { SelectMenu } from 'components';
import { ProfileOwnedFeed } from './ProfileOwnedFeed';
import { ProfileCommonFeed } from './ProfileCommonFeed';

import Icon from 'assets/icons';

import styles from './ProfileFeed.module.scss';

interface ProfileActivityFeedProps {
  isCurrentUser: boolean;
}

const ProfileNameQuery = gql`
  query getProfileName($userId: String!) {
    profileByIdentifier(identifier: $userId) {
      ... on Person {
        name
      }
    }
  }
`;

export const ProfileActivityFeed: React.FC<ProfileActivityFeedProps> = ({ isCurrentUser }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { userId } = useParams();
  const { groupsFeatureFlag } = useFeatureFlagState();

  const showCommon = useQueryParams().get('common') === 'true';

  const { error, data } = useQuery(ProfileNameQuery, {
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
    variables: { userId },
  });

  useEffect(() => {
    trackingDocumentListProfileFeed(userId as string, 'default', groupsFeatureFlag);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  if (!data || error) {
    return null;
  }

  /**
   * Renders the "User's Documents" dropdown option
   */
  const renderOwnedFilter = () => {
    return (
      <div
        className={classNames(styles.filterMenuButton, {
          [styles.active]: !showCommon,
        })}
      >
        <Icon.Checkmark />{' '}
        {t('profile-screen.profile-feed.owned-label', { name: getFormattedName() })}
      </div>
    );
  };

  /**
   * Renders the "Documents in Common" dropdown option
   */
  const renderCommonFilter = () => {
    return (
      <div
        className={classNames(styles.filterMenuButton, {
          [styles.active]: showCommon,
        })}
      >
        <Icon.Checkmark />{' '}
        {t('profile-screen.profile-feed.common-label', { name: getFormattedName() })}
      </div>
    );
  };

  /**
   * Called when a dropdown filter option is clicked
   * @param e
   */
  const handleFilterChange = (e: any) => {
    const { pathname } = history.location;
    const url = e.key === 'common' ? `${pathname}?common=true` : pathname;
    history.push(url);
  };

  /**
   * Formats the profile name to title case
   */
  const getFormattedName = () => {
    return toTitleCase(data.profileByIdentifier.name);
  };

  /**
   * Generates the heading text that is rendered on top of the document listing
   */
  const getHeadingText = () => {
    const key = showCommon ? 'common-label' : 'owned-label';
    return t(`profile-screen.profile-feed.${key}`, { name: getFormattedName() });
  };

  return (
    <div className={styles.tetrisMain}>
      <div className={styles.headingWrapper}>
        <h3 className={styles.heading}>{getHeadingText()}</h3>

        <div className={styles.filter}>
          <SelectMenu
            closeOnClick
            className={styles.filterButton}
            buttonActiveClass={styles.active}
            menuClassName={styles.filterMenu}
            id="profile-filter"
            arrow={false}
            label={<Icon.Actions className={styles.filterButtonIcon} />}
            horizontalPosition="Right"
            options={[
              {
                key: 'owned',
                value: renderOwnedFilter(),
              },
              {
                key: 'common',
                value: renderCommonFilter(),
              },
            ]}
            onChange={handleFilterChange}
          />
        </div>
      </div>

      {showCommon ? (
        <ProfileCommonFeed isCurrentUser={isCurrentUser} />
      ) : (
        <ProfileOwnedFeed isCurrentUser={isCurrentUser} />
      )}
    </div>
  );
};
